import PortOpeningRuleOnPrem from './PortOpeningRuleOnPrem';
import WorkflowInstance from '../OnPremWorkflowInstance';
import User from '../User';
import { WorkflowState } from '../enums/WorkflowState';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../enums/UserRole';
import { PortOpeningRequestsDirection } from '../enums/POR/PortOpeningRequestsDirection';
import CartaId from './CartaId';
import { OnPremPortOpeningRequestsTypes } from '../enums/POR/OnPremPortOpeningRequestsTypes';
import CartaIdComponent from '../viewModels/CartaId/CartaIdComponent';
import ApprovalContact, { fromUser, fromEmails, fromAppEndorser } from '../interfaces/ApprovalContact';
import { ApprovalContactLabels } from '../ApprovalContactLabels';

export default class PortOpeningRequestOnPrem {
    id!: number;

	type!: OnPremPortOpeningRequestsTypes;

    trackerID!: number;

	firewall!: string; //Firewall

	firewallEnvironment!: string;

	siteCoordination!: boolean;

	cartaId!: CartaId[];

	appEndorser!: CartaIdComponent;

	onPremPortOpeningRules!: PortOpeningRuleOnPrem[];

	workflowInstance!: WorkflowInstance;

	direction!: PortOpeningRequestsDirection;

    contactUser!: User;

    submitDate!: Date;

    businessJustification!: string;

    comments!: string;

    approvingUser!: User;

	approvingEmail!: string;

	ownerDelegateUser!: string;

	currentApproverDistributionList: string[];

    constructor(obj: any) {
		Object.assign(this, obj);
		this.workflowInstance = new WorkflowInstance(obj.workflowInstance);
		this.type = new OnPremPortOpeningRequestsTypes().fromName(obj.type?.name);
		this.direction = new PortOpeningRequestsDirection().fromName(obj.direction?.name);
		const customFields = new CartaIdComponent(obj.appEndorser).fromUser(obj.appEndorser);
		this.appEndorser = new CartaIdComponent({
			...obj.appEndorser,
			customFields: customFields,
		});
	}

	isRequestorUser() {
		return authenticatedUser.userId === this.workflowInstance.createdBy.id;
	}	

	currentApprovers(): ApprovalContact[] {
		let approvers: ApprovalContact[] = [];
		const distributionList = fromEmails(this.currentApproverDistributionList);

		switch (this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState?.value) {
			case WorkflowState.PendingForPolicyTeamApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicyTeam, email: distributionList }];
				break;
			case WorkflowState.PendingSolutionArchitectureApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicySolutionArchitects, email: distributionList }];
				break;
			case WorkflowState.PendingForCyberApproval.value:
				approvers = [{ name: ApprovalContactLabels.CyberSecurityTeam, email: distributionList }];
				break;
			case WorkflowState.PendingProxyArchitectApproval.value:
				approvers = [{ name: ApprovalContactLabels.ProxyArchitects, email: distributionList }];
				break;
			case WorkflowState.PendingOTCRMApproval.value:
				approvers = [{ name: ApprovalContactLabels.OTCybersecurityRiskManagement, email: distributionList }];
				break;
			case WorkflowState.PendingConnOwnerApproval.value:
			case WorkflowState.PendingForDOAGApproval.value:
				approvers = [fromUser(this.approvingUser)];
				break;
			case WorkflowState.PendingAppEndorserApproval.value:
				approvers = [fromAppEndorser(this.appEndorser)];
				break;
		}

		return approvers.filter((approver) => !!approver?.name);
	}

	isApprovingUser() {
		return authenticatedUser.userId === this.approvingUser?.id
	}

	isInEditableState() {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return currentState == WorkflowState.Review.value || currentState == WorkflowState.Draft.value;
	}

	isInmutableState() {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return currentState == WorkflowState.Completed.value  || currentState == WorkflowState.Rejected.value || 
			   currentState == WorkflowState.Cancelled.value || currentState == WorkflowState.ImplementationFailed.value;
	}

	isEditable(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			this.isRequestorUser() &&
			[
				WorkflowState.Draft.value,
				WorkflowState.Review.value,
			].includes(currentState)
		);
	}

	canHaveAppOwnerApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.PendingAppEndorserApproval.value &&
			(authenticatedUser.userEmail === this.appEndorser?.customFields.contact_email)
		);
	}

    canHaveDOAGApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === (WorkflowState.PendingForDOAGApproval.value) &&
			this.isApprovingUser()
		);
	}

	canHaveConnOwnerApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === (WorkflowState.PendingConnOwnerApproval.value) &&
			this.isApprovingUser()
		);
	}

    canHaveReviewerApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.PendingForPolicyTeamApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer)
		);
    }

	canHaveImplementerApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.AwaitingImplementation.value && authenticatedUser.isInRole(UserRole.PORToOnPremTechImplementer)
		);
    }

	canHaveProxyApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.AwaitingImplementation.value && authenticatedUser.isInRole(UserRole.POROnPremIBossTechnicalImplementer)
		);
    }

	canHaveCyberSecApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.PendingForCyberApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremCyberReviewer)
		)
	}

	canHaveProxyArchApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.PendingProxyArchitectApproval.value && authenticatedUser.isInRole(UserRole.POROnPremProxyArchitectReviewer)
		)
	}

	canEscalateApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.PendingForPolicyTeamApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer)
		)
	}

	canEscalateToCyberSec(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return currentState === WorkflowState.PendingSolutionArchitectureApproval.value && authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer)
	}

	canHaveOTCRMApproval(): boolean {
        const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
        return (
            currentState === WorkflowState.PendingOTCRMApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremOTCRM) && this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection
        );
    }

	canHaveSolutionArchitectApproval(): boolean {
        const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
            currentState === WorkflowState.PendingSolutionArchitectureApproval.value && authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer)
        );
    }

	canContinueRequest(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			currentState === WorkflowState.Completed.value ||
			currentState === WorkflowState.ImplementationFailed.value
		)
	}

	canSendToReview(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			(!this.isInmutableState() && !this.isInEditableState() && !(currentState === WorkflowState.AwaitingImplementation.value) && authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer)) ||
			(currentState === WorkflowState.PendingSolutionArchitectureApproval.value && authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer)) ||
			(currentState === WorkflowState.PendingProxyArchitectApproval.value && authenticatedUser.isInRole(UserRole.POROnPremProxyArchitectReviewer)) ||
			(currentState === WorkflowState.PendingForCyberApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremCyberReviewer)) ||
			(currentState === WorkflowState.PendingOTCRMApproval.value && authenticatedUser.isInRole(UserRole.PORToOnPremOTCRM) && this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection) ||
			(currentState === WorkflowState.PendingAppEndorserApproval.value && (authenticatedUser.userId === this.appEndorser?.id) && this.type === OnPremPortOpeningRequestsTypes.InternetDMZ) ||
			(currentState === WorkflowState.PendingConnOwnerApproval.value && (authenticatedUser.userId === this.approvingUser?.id) && this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection)
		)
	}

	canSendToDraft(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			(!this.isInmutableState() && !this.isInEditableState() && this.isRequestorUser())
		)
	}

	isCancellable(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		return (
			(!this.isInmutableState() && !(currentState === WorkflowState.AwaitingImplementation.value)) &&
			(
				this.isRequestorUser() ||
				authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer) ||
				(currentState === WorkflowState.PendingSolutionArchitectureApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer))) ||
				(currentState === WorkflowState.PendingProxyArchitectApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer))) ||
				(currentState === WorkflowState.PendingForCyberApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer))) ||
				(currentState === WorkflowState.PendingOTCRMApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer))) ||
				(currentState === WorkflowState.PendingForDOAGApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer))) ||
				(currentState === WorkflowState.PendingAppEndorserApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer) || authenticatedUser.userId === this.workflowInstance.createdBy.id) && this.type === OnPremPortOpeningRequestsTypes.InternetDMZ) ||
				(currentState === WorkflowState.PendingConnOwnerApproval.value && (authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer) || authenticatedUser.userId === this.workflowInstance.createdBy.id) && this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection)
			)
		)
	}

	canCreateCHG(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		const ticketId = this.workflowInstance.currentWorkflowInstanceState.ticketId
		return (
			(!this.isInmutableState() && (currentState === WorkflowState.AwaitingImplementation.value)) && authenticatedUser.isInRole(UserRole.SSPDeveloper) && !ticketId 
		)
	}
}